import { BackendError, Service } from '@genial/services';
import { MetaResponse } from '@genial/services/dist/models';
import { selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { transformSortItem } from '../../../utils/transformSortItem';
import { publicOffersLoadedState } from '../atom';
import { screenNameCustomerApplication } from '../constants';
import { execFilter } from '../external/filters';
import { execSort } from '../external/sort';
import {
  ReturnTypeOf_ObterDetalhesOfertaPublica,
  ReturnTypeOf_ProdutosObterProdutosDisponiveis,
  ReturnTypeOf_SolicitarAtivoParaAplicacao,
} from '../external/types';
import {
  normalizePublicOfferAssetToInvestDetails,
  normalizePublicOfferDetails,
  normalizePublicOffers,
} from '../normalizer';
import {
  DynamicAssetsQueryCustomerParams,
  DynamicAssetsQueryParams,
  DynamicCustomFiltersProps,
  PublicOfferAssetToInvestNormalized,
  PublicOfferDetailsNormalized,
  PublicOfferEntityNormalized,
} from '../types';

export const publicOffersQueryUpdated = selectorFamily<
  { data: PublicOfferEntityNormalized[]; meta: MetaResponse },
  DynamicAssetsQueryParams
>({
  key: 'CustomerApplication/publicOffersQueryUpdated',
  get: ({ accountId, filtersToApply, searchTerm, orderBy }) => {
    return async ({ get }) => {
      try {
        const loadedPublicOffers = get(publicOffersLoadedState);
        const sortItems = transformSortItem(orderBy);

        let data: PublicOfferEntityNormalized[] = [];

        if (!loadedPublicOffers.data.length || searchTerm) {
          const apiData =
            await Service.External.Extranet.Produtos.V1.produtosObterProdutosDisponiveis({
              filtro: {
                idConta: accountId,
                nomeProduto: searchTerm,
                tipoProduto: 5,
              },
              screenName: screenNameCustomerApplication,
            });

          data = normalizePublicOffers(apiData as ReturnTypeOf_ProdutosObterProdutosDisponiveis);
        } else {
          data = loadedPublicOffers.data;
        }

        if (filtersToApply) {
          data = execFilter(data, filtersToApply);
        }

        if (sortItems && sortItems.sort && sortItems.sortDirection) {
          data = execSort(data, sortItems);
        }

        return { data, meta: blankMetaResponse() };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const publicOffersCustomFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  DynamicAssetsQueryCustomerParams
>({
  key: 'CustomerApplication/publicOffersCustomFiltersQuery',
  get: () => {
    return async ({ get }) => {
      const loadedPublicOffers = get(publicOffersLoadedState);

      if (!loadedPublicOffers.data.length) {
        return { filters: [] };
      }

      const offerTypeOptions = loadedPublicOffers.data.reduce((acc, { offerType, offerTypeId }) => {
        const existingOption = acc.find((option) => option.value === offerTypeId);
        if (!existingOption) {
          acc.push({ label: offerType, value: offerTypeId });
        }
        return acc;
      }, [] as { label: string; value: string }[]);

      const suitabilityOptions = loadedPublicOffers.data.reduce((acc, { suitabilityProfile }) => {
        const existingOption = acc.find((option) => option.value === suitabilityProfile);
        if (!existingOption) {
          acc.push({ label: suitabilityProfile, value: suitabilityProfile });
        }
        return acc;
      }, [] as { label: string; value: string }[]);

      const limiterOptions = loadedPublicOffers.data.reduce((acc, { limiter }) => {
        const existingOption = acc.find((option) => option.value === limiter);
        if (!existingOption) {
          acc.push({ label: limiter, value: limiter });
        }
        return acc;
      }, [] as { label: string; value: string }[]);

      return {
        filters: [
          { fieldName: 'offerTypeId', label: 'Tipo da oferta', options: offerTypeOptions },
          {
            fieldName: 'suitabilityProfile',
            label: 'Perfil de risco',
            options: suitabilityOptions,
          },
          {
            fieldName: 'limiter',
            label: 'Limitador',
            options: limiterOptions,
          },
        ],
      };
    };
  },
});

export const publicOfferDetailsQuery = selectorFamily<
  PublicOfferDetailsNormalized | null | undefined,
  { offerId?: number }
>({
  key: 'CustomerApplication/publicOfferDetailsQuery',
  get: ({ offerId }) => {
    return async () => {
      if (!offerId) {
        return;
      }

      const apiData =
        await Service.External.Extranet.RendaVariavel.V1.rendaVariavelObterDetalhesOfertaPublica({
          detalhes: { numeroOferta: offerId },
          screenName: screenNameCustomerApplication,
        });

      const normalizedData = normalizePublicOfferDetails(
        apiData as ReturnTypeOf_ObterDetalhesOfertaPublica
      );

      return normalizedData;
    };
  },
});

export const publicOfferAssetToInvestQuery = selectorFamily<
  PublicOfferAssetToInvestNormalized | undefined,
  { cpf: string; offerId: number; accountId: number }
>({
  key: 'CustomerApplication/publicOfferAssetToInvestQuery',
  get: ({ cpf, offerId, accountId }) => {
    return async () => {
      if (!offerId) {
        return;
      }

      const apiData =
        await Service.External.Extranet.Produtos.V1.produtosSolicitarAtivoParaAplicacao({
          ativoSolicitar: {
            cpf,
            idAtivo: offerId,
            idConta: accountId,
            tipoProduto: 5,
          },
          screenName: screenNameCustomerApplication,
        });

      const normalizedData = normalizePublicOfferAssetToInvestDetails(
        apiData as ReturnTypeOf_SolicitarAtivoParaAplicacao
      );

      return normalizedData;
    };
  },
});
